import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import * as api from './api';

const NPC_PLOT_IDS = [1, 3];

const lsKey = (eventId, playerId) => `em-att-${eventId}-${playerId || 'new'}`;
const saveLocal = (eventId, playerId, data) => { try { localStorage.setItem(lsKey(eventId, playerId), JSON.stringify(data)); } catch {} };
const loadLocal = (eventId, playerId) => { try { const r = localStorage.getItem(lsKey(eventId, playerId)); return r ? JSON.parse(r) : null; } catch { return null; } };
const clearLocal = (eventId, playerId) => { try { localStorage.removeItem(lsKey(eventId, playerId)); } catch {} };

const AttendanceRow = ({
    mode, attendee, players, attendeeTypes, eventDays, eventStartDate, eventId,
    addedPlayerIds, onSave, onCancel, onDelete, token
}) => {
    const isAdd = mode === 'add';
    const [expanded, setExpanded] = useState(isAdd);
    const [saving, setSaving] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showNoteDeleteIdx, setShowNoteDeleteIdx] = useState(null);
    const [dirty, setDirty] = useState(false);

    const cached = useMemo(() => isAdd ? loadLocal(eventId, null) : null, []);

    const [playerId, setPlayerId] = useState(cached?.playerId || attendee?.playerId || null);
    const [characterId, setCharacterId] = useState(cached?.characterId || attendee?.characterId || null);
    const [attendeeTypeId, setAttendeeTypeId] = useState(cached?.attendeeTypeId || attendee?.attendeeTypeId || 2);
    const [daysAttended, setDaysAttended] = useState(cached?.daysAttended ?? attendee?.daysAttended ?? eventDays);
    const [npcShifts, setNpcShifts] = useState(cached?.npcShifts ?? 0);
    const [didRegistrationFeedback, setDidRegistrationFeedback] = useState(cached?.didRegistrationFeedback ?? false);
    const [deaths, setDeaths] = useState(cached?.deaths ?? attendee?.deaths ?? 0);
    const [spiritBottleDeaths, setSpiritBottleDeaths] = useState(cached?.spiritBottleDeaths ?? attendee?.spiritBottleDeaths ?? 0);
    const [bankChange, setBankChange] = useState(cached?.bankChange ?? attendee?.bankChange ?? 0);
    const [spiritBottleCharges, setSpiritBottleCharges] = useState(cached?.spiritBottleCharges ?? attendee?.spiritBottleCharges ?? 0);
    const [plotNotes, setPlotNotes] = useState(cached?.plotNotes || attendee?.plotNotes || []);
    const [newNoteText, setNewNoteText] = useState('');
    const [newNoteDate, setNewNoteDate] = useState(eventStartDate || '');
    const [newNoteDateExpires, setNewNoteDateExpires] = useState('');
    const [editingNoteIdx, setEditingNoteIdx] = useState(null);

    const [characters, setCharacters] = useState([]);
    const [loadingChars, setLoadingChars] = useState(false);
    const [playerSearch, setPlayerSearch] = useState('');
    const [playerDropdownOpen, setPlayerDropdownOpen] = useState(false);
    const [highlightIdx, setHighlightIdx] = useState(0);

    const playerDropdownRef = useRef(null);
    const playerInputRef = useRef(null);
    const charSelectRef = useRef(null);
    const noteInputRef = useRef(null);

    useEffect(() => {
        if (attendee?.spLog) {
            const npc = attendee.spLog.find(s => s.spTypeName === 'NPC SHIFT');
            setNpcShifts(npc?.quantity || 0);
            const reg = attendee.spLog.find(s => s.spTypeName === 'REGISTRATION/FEEDBACK');
            setDidRegistrationFeedback(reg?.quantity > 0 || false);
        }
    }, [attendee]);

    const isNpcOrPlot = NPC_PLOT_IDS.includes(attendeeTypeId);
    useEffect(() => { if (isNpcOrPlot) setNpcShifts(daysAttended); }, [isNpcOrPlot, daysAttended]);

    useEffect(() => {
        if (!playerId) { setCharacters([]); setCharacterId(null); return; }
        let c = false;
        (async () => {
            setLoadingChars(true);
            try {
                const chars = await api.fetchCharactersForPlayer(playerId);
                if (!c) { setCharacters(chars || []); if (chars.length === 1) setCharacterId(chars[0].characterId); else if (!chars.find(x => x.characterId === characterId)) setCharacterId(null); }
            } catch { if (!c) setCharacters([]); }
            finally { if (!c) setLoadingChars(false); }
        })();
        return () => { c = true; };
    }, [playerId, token]);

    useEffect(() => {
        const h = (e) => { if (playerDropdownRef.current && !playerDropdownRef.current.contains(e.target)) setPlayerDropdownOpen(false); };
        document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
    }, []);

    useEffect(() => { if (isAdd && playerInputRef.current) setTimeout(() => playerInputRef.current?.focus(), 50); }, [isAdd]);

    const filteredPlayers = useMemo(() => {
        const q = playerSearch.trim().toLowerCase();
        let list = players;
        if (isAdd) list = list.filter(p => !addedPlayerIds.has(p.playerId));
        if (q) list = list.filter(p => (p.name || '').toLowerCase().includes(q));
        return list;
    }, [players, playerSearch, addedPlayerIds, isAdd]);

    useEffect(() => { setHighlightIdx(0); }, [filteredPlayers.length]);

    const selectedPlayerName = useMemo(() => players.find(p => p.playerId === playerId)?.name || '', [playerId, players]);

    const buildPayload = useCallback(() => ({
        characterId: characterId || null, attendeeTypeId, daysAttended, npcShifts,
        didRegistrationFeedback, deaths, spiritBottleDeaths, bankChange, spiritBottleCharges,
        plotNotes: plotNotes.map(pn => ({ note: pn.note, date: pn.date || eventStartDate || null, dateExpires: pn.dateExpires || null }))
    }), [characterId, attendeeTypeId, daysAttended, npcShifts, didRegistrationFeedback, deaths, spiritBottleDeaths, bankChange, spiritBottleCharges, plotNotes, eventStartDate]);

    // Local storage backup
    useEffect(() => {
        if (isAdd && playerId) saveLocal(eventId, null, { playerId, characterId, attendeeTypeId, daysAttended, npcShifts, didRegistrationFeedback, deaths, spiritBottleDeaths, bankChange, spiritBottleCharges, plotNotes });
    }, [isAdd, playerId, characterId, attendeeTypeId, daysAttended, npcShifts, didRegistrationFeedback, deaths, spiritBottleDeaths, bankChange, spiritBottleCharges, plotNotes, eventId]);

    // Auto-save for edit mode
    const saveTimeoutRef = useRef(null);
    useEffect(() => {
        if (isAdd || !dirty || !playerId) return;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(async () => {
            setSaving(true);
            const ok = await onSave(playerId, buildPayload());
            setSaving(false); setDirty(false);
            if (!ok) saveLocal(eventId, playerId, buildPayload());
        }, 800);
        return () => { if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current); };
    }, [dirty, characterId, attendeeTypeId, daysAttended, npcShifts, didRegistrationFeedback, deaths, spiritBottleDeaths, bankChange, spiritBottleCharges, plotNotes]);

    const markDirty = () => { if (!isAdd) setDirty(true); };
    const setCharIdD = (v) => { setCharacterId(v); markDirty(); };
    const setTypeD = (v) => { setAttendeeTypeId(v); markDirty(); };
    const setDaysD = (v) => { setDaysAttended(v); markDirty(); };
    const setNpcD = (v) => { setNpcShifts(v); markDirty(); };
    const setRegD = (v) => { setDidRegistrationFeedback(v); markDirty(); };
    const setDeathsD = (v) => { setDeaths(v); markDirty(); };
    const setSpiritBottleDeathsD = (v) => { setSpiritBottleDeaths(v); markDirty(); };
    const setBankD = (v) => { setBankChange(v); markDirty(); };
    const setSpiritBottleChargesD = (v) => { setSpiritBottleCharges(v); markDirty(); };

    const selectPlayer = (p) => { setPlayerId(p.playerId); setPlayerSearch(''); setPlayerDropdownOpen(false); setTimeout(() => charSelectRef.current?.focus(), 100); };

    const handlePlayerKeyDown = (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setPlayerDropdownOpen(true); setHighlightIdx(p => Math.min(p + 1, filteredPlayers.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(p => Math.max(p - 1, 0)); }
        else if (e.key === 'Enter' || e.key === 'Tab') { if (playerDropdownOpen && filteredPlayers.length > 0) { e.preventDefault(); selectPlayer(filteredPlayers[highlightIdx] || filteredPlayers[0]); } }
        else if (e.key === 'Escape') setPlayerDropdownOpen(false);
    };

    const handleAddNote = () => {
        const text = newNoteText.trim(); if (!text) return;
        setPlotNotes(prev => [...prev, { note: text, date: newNoteDate || eventStartDate || null, dateExpires: newNoteDateExpires || null }]);
        const prevExp = newNoteDateExpires;
        setNewNoteText(''); setNewNoteDate(eventStartDate || ''); setNewNoteDateExpires(prevExp);
        markDirty(); setTimeout(() => noteInputRef.current?.focus(), 30);
    };

    const confirmRemoveNote = (idx) => setShowNoteDeleteIdx(idx);
    const doRemoveNote = () => {
        if (showNoteDeleteIdx !== null) { setPlotNotes(prev => prev.filter((_, i) => i !== showNoteDeleteIdx)); setEditingNoteIdx(null); markDirty(); }
        setShowNoteDeleteIdx(null);
    };

    const handleNoteUpdate = (idx, field, value) => { setPlotNotes(prev => prev.map((pn, i) => i === idx ? { ...pn, [field]: value } : pn)); markDirty(); };

    const handleNoteInputKeyDown = (e) => { if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey && !e.metaKey) { e.preventDefault(); e.stopPropagation(); handleAddNote(); } };

    const handleFormKeyDown = (e) => { if (isAdd && (e.ctrlKey || e.shiftKey || e.metaKey) && e.key === 'Enter' && playerId) { e.preventDefault(); e.stopPropagation(); handleSaveAdd(); } };

    const handleSaveAdd = async () => {
        if (!playerId) return; setSaving(true);
        const ok = await onSave(playerId, buildPayload()); setSaving(false);
        if (ok) {
            clearLocal(eventId, null);
            setPlayerId(null); setCharacterId(null); setAttendeeTypeId(2); setDaysAttended(eventDays);
            setNpcShifts(0); setDidRegistrationFeedback(false); setDeaths(0); setSpiritBottleDeaths(0); setBankChange(0); setSpiritBottleCharges(0);
            setPlotNotes([]); setNewNoteText(''); setNewNoteDate(eventStartDate || ''); setNewNoteDateExpires('');
            setPlayerSearch(''); setEditingNoteIdx(null);
            setTimeout(() => playerInputRef.current?.focus(), 80);
        }
    };

    // Collapsed view
    if (!isAdd && !expanded) {
        const typeName = attendeeTypes.find(t => t.attendeeTypeId === attendee?.attendeeTypeId)?.name || '?';
        return (
            <div className="em-att-row em-att-collapsed" onClick={() => setExpanded(true)}>
                <span className="em-att-name">{attendee?.playerName}</span>
                <span className="em-att-badge">{typeName}</span>
                <span className="em-att-detail">{attendee?.daysAttended} day{attendee?.daysAttended !== 1 ? 's' : ''}</span>
                {attendee?.deaths > 0 && <span className="em-att-death">☠ {attendee.deaths}</span>}
                {attendee?.spiritBottleDeaths > 0 && <span className="em-att-spirit-death">🍾 {attendee.spiritBottleDeaths}</span>}
                {saving && <span className="em-att-saving">saving…</span>}
                <span className="em-att-expand">▼</span>
            </div>
        );
    }

    return (
        <div className={`em-att-row em-att-expanded ${isAdd ? 'em-att-add' : ''}`} onKeyDown={handleFormKeyDown}>
            {!isAdd && (
                <div className="em-att-row-header" onClick={() => setExpanded(false)}>
                    <span className="em-att-name">{attendee?.playerName}</span>
                    {saving && <span className="em-att-saving">saving…</span>}
                    <span className="em-att-expand">▲</span>
                </div>
            )}

            <div className="em-att-form">
                {isAdd && (
                    <div className="em-att-field em-att-field-wide">
                        <label className="em-att-label">Player</label>
                        <div className="em-player-dropdown" ref={playerDropdownRef}>
                            <input ref={playerInputRef} type="text" className="em-form-input"
                                placeholder="Search players…"
                                value={playerDropdownOpen ? playerSearch : selectedPlayerName}
                                onChange={(e) => { setPlayerSearch(e.target.value); setPlayerDropdownOpen(true); }}
                                onFocus={() => { setPlayerSearch(''); setPlayerDropdownOpen(true); }}
                                onKeyDown={handlePlayerKeyDown} autoFocus />
                            {playerDropdownOpen && (
                                <div className="em-player-options">
                                    {filteredPlayers.length === 0 ? (
                                        <div className="em-player-option em-player-empty">No matches</div>
                                    ) : filteredPlayers.map((p, i) => (
                                        <div key={p.playerId}
                                            className={`em-player-option ${i === highlightIdx ? 'em-highlighted' : ''}`}
                                            onClick={() => selectPlayer(p)} onMouseEnter={() => setHighlightIdx(i)}>
                                            {p.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="em-att-field">
                    <label className="em-att-label">Character</label>
                    <select ref={charSelectRef} className={isAdd ? 'em-form-input' : 'em-inline-select'}
                        value={characterId || ''} onChange={(e) => setCharIdD(e.target.value ? parseInt(e.target.value, 10) : null)}
                        disabled={!playerId || loadingChars}>
                        <option value="">{loadingChars ? 'Loading…' : '—'}</option>
                        {characters.map(c => <option key={c.characterId} value={c.characterId}>{c.name}</option>)}
                    </select>
                </div>

                <div className="em-att-field">
                    <label className="em-att-label">Type</label>
                    <select className={isAdd ? 'em-form-input' : 'em-inline-select'} value={attendeeTypeId}
                        onChange={(e) => setTypeD(parseInt(e.target.value, 10))}>
                        {attendeeTypes.map(t => <option key={t.attendeeTypeId} value={t.attendeeTypeId}>{t.name}</option>)}
                    </select>
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">Days</label>
                    <input type="text" inputMode="numeric" pattern="[0-9]*"
                        className={isAdd ? 'em-form-input em-form-number' : 'em-inline-input em-form-number'}
                        value={daysAttended} onChange={(e) => setDaysD(parseInt(e.target.value, 10) || 0)} />
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">NPC Shifts</label>
                    <input type="text" inputMode="numeric" pattern="[0-9]*"
                        className={isAdd ? 'em-form-input em-form-number' : 'em-inline-input em-form-number'}
                        value={npcShifts} onChange={(e) => setNpcD(parseInt(e.target.value, 10) || 0)} disabled={isNpcOrPlot} />
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">Deaths</label>
                    <input type="text" inputMode="numeric" pattern="[0-9]*"
                        className={isAdd ? 'em-form-input em-form-number' : 'em-inline-input em-form-number'}
                        value={deaths} onChange={(e) => setDeathsD(parseInt(e.target.value, 10) || 0)} />
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">SB Deaths</label>
                    <input type="text" inputMode="numeric" pattern="[0-9]*"
                        className={isAdd ? 'em-form-input em-form-number' : 'em-inline-input em-form-number'}
                        value={spiritBottleDeaths} onChange={(e) => setSpiritBottleDeathsD(parseInt(e.target.value, 10) || 0)} />
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">Bank</label>
                    <input type="text" inputMode="numeric" pattern="-?[0-9]*"
                        className={isAdd ? 'em-form-input em-form-number' : 'em-inline-input em-form-number'}
                        value={bankChange} onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || v === '-') { setBankD(v); return; }
                            setBankD(parseInt(v, 10) || 0);
                        }} />
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">SB Charges</label>
                    <input type="text" inputMode="numeric" pattern="-?[0-9]*"
                        className={isAdd ? 'em-form-input em-form-number' : 'em-inline-input em-form-number'}
                        value={spiritBottleCharges} onChange={(e) => {
                            const v = e.target.value;
                            if (v === '' || v === '-') { setSpiritBottleChargesD(v); return; }
                            setSpiritBottleChargesD(parseInt(v, 10) || 0);
                        }} />
                </div>

                <div className="em-att-field em-att-field-narrow">
                    <label className="em-att-label">Pre-reg</label>
                    <button type="button" className={`em-toggle ${didRegistrationFeedback ? 'em-toggle-on' : 'em-toggle-off'}`}
                        onClick={() => setRegD(!didRegistrationFeedback)}>
                        {didRegistrationFeedback ? 'Yes' : 'No'}
                    </button>
                </div>
            </div>

            {/* Plot notes */}
            <div className="em-att-notes">
                <label className="em-att-label">Plot Notes</label>
                <div className="em-notes-list">
                    {plotNotes.map((pn, idx) => (
                        <div key={idx} className="em-note-item">
                            {editingNoteIdx === idx ? (
                                <input type="text" className="em-form-input em-note-edit-input" value={pn.note}
                                    onChange={(e) => handleNoteUpdate(idx, 'note', e.target.value)}
                                    onBlur={() => setEditingNoteIdx(null)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setEditingNoteIdx(null); } }}
                                    autoFocus />
                            ) : (
                                <span className="em-note-text" onClick={() => setEditingNoteIdx(idx)} title="Click to edit">{pn.note}</span>
                            )}
                            <input type="date" className="em-form-input em-note-date" value={pn.date || ''}
                                onChange={(e) => handleNoteUpdate(idx, 'date', e.target.value)} title="Start date" />
                            <input type="date" className={`em-form-input em-note-date ${!pn.dateExpires ? 'em-date-empty' : ''}`}
                                value={pn.dateExpires || ''}
                                onChange={(e) => handleNoteUpdate(idx, 'dateExpires', e.target.value)} title="End date" />
                            <button type="button" className="em-note-remove" onClick={() => confirmRemoveNote(idx)} title="Remove note">✕</button>
                        </div>
                    ))}
                </div>
                <div className="em-note-add">
                    <input ref={noteInputRef} type="text" className="em-form-input em-note-add-text"
                        placeholder="Add a plot note…" value={newNoteText}
                        onChange={(e) => setNewNoteText(e.target.value)} onKeyDown={handleNoteInputKeyDown} />
                    <input type="date" className="em-form-input em-note-date" value={newNoteDate}
                        onChange={(e) => setNewNoteDate(e.target.value)} title="Start date" />
                    <input type="date" className={`em-form-input em-note-date ${!newNoteDateExpires ? 'em-date-empty' : ''}`}
                        value={newNoteDateExpires} onChange={(e) => setNewNoteDateExpires(e.target.value)} title="End date" />
                </div>
            </div>

            {/* Actions */}
            <div className="em-att-actions">
                {isAdd && (
                    <>
                        <button className="em-btn em-btn-primary" onClick={handleSaveAdd} disabled={saving || !playerId}>
                            {saving ? 'Saving…' : 'Save & Add Another'}
                        </button>
                        <button className="em-btn em-btn-secondary" onClick={onCancel}>Done Adding</button>
                        <span className="em-att-hint">Ctrl+Enter to save</span>
                    </>
                )}
                {!isAdd && onDelete && (
                    <button className="em-btn em-btn-danger em-btn-small" onClick={() => setShowDeleteConfirm(true)}>Remove from Event</button>
                )}
            </div>

            {/* Delete attendee modal */}
            {showDeleteConfirm && (
                <div className="em-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="em-modal em-confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="em-confirm-title">Remove Attendee?</h3>
                        <p className="em-confirm-text">Remove <strong>{attendee?.playerName || 'this player'}</strong> from this event? This deletes their attendance, SP log, death log, bank log, spirit bottle log, and plot notes.</p>
                        <div className="em-form-actions">
                            <button className="em-btn em-btn-danger" onClick={() => { setShowDeleteConfirm(false); onDelete(); }}>Remove Attendee</button>
                            <button className="em-btn em-btn-secondary" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete note confirmation modal */}
            {showNoteDeleteIdx !== null && (
                <div className="em-modal-overlay" onClick={() => setShowNoteDeleteIdx(null)}>
                    <div className="em-modal em-confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3 className="em-confirm-title">Remove Plot Note?</h3>
                        <p className="em-confirm-text">Remove this note: "{plotNotes[showNoteDeleteIdx]?.note}"?</p>
                        <div className="em-form-actions">
                            <button className="em-btn em-btn-danger" onClick={doRemoveNote}>Remove Note</button>
                            <button className="em-btn em-btn-secondary" onClick={() => setShowNoteDeleteIdx(null)}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AttendanceRow;
